import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  INSURANCE_AR_PIE_COLOR,
  PATIENT_AR_PIE_COLOR,
  RECEIVABLES_RISK_FILTERS,
  RECEIVABLES_RISK_KPI_DEFINITIONS,
  type HighBalancePatientRow,
} from '@constants/Reports/receivablesRiskAnalysis.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useReceivablesRiskFinance } from '@hooks/reports/useReceivablesRiskFinance'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { formatCurrency } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  formatKpiCurrency,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_LINE_CHART_MARGIN = { top: 16, right: 12, left: 4, bottom: 8 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 52

function formatDollarK(value: number): string {
  if (value === 0) return '$0'
  const k = value / 1000
  return Number.isInteger(k) ? `$${k}K` : `$${k.toFixed(1)}K`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function formatKpiDays(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return value.toFixed(1)
}

function computeDaysAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 40 : Math.ceil((max * 1.15) / 10) * 10
  const step = axisMax <= 40 ? 10 : axisMax <= 80 ? 20 : 25
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function ReceivablesRiskAnalysisReport(): React.ReactElement {
  const [agingView, setAgingView] = useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const highBalanceFullView = useReportFullViewSection('rra-high-balance')
  const { loading, error, data, bucketLabel } = useReceivablesRiskFinance(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load receivables risk data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return RECEIVABLES_RISK_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'total-ar': data.totalAr.value,
      'insurance-ar': data.insuranceAr.value,
      'patient-ar': data.patientAr.value,
      'ar-over-90': data.arOver90.value,
      'avg-days-in-ar': data.averageDaysInAr.value,
    }

    return RECEIVABLES_RISK_KPI_DEFINITIONS.map((def) => {
      const raw = valueById[def.id]
      let value: string
      if (def.format === 'currency') value = formatKpiCurrency(raw)
      else if (def.format === 'percent') value = formatKpiPercent(raw)
      else value = formatKpiDays(raw)

      return {
        id: def.id,
        title: def.title,
        footer: def.footer,
        value,
      }
    })
  }, [data, loading])

  const arTrendData = useMemo(() => {
    if (!data) return []
    return data.arTrendByPeriod.buckets.map((row) => ({
      month: row.label,
      amount: row.value,
      label: formatDollarK(row.value),
    }))
  }, [data])

  const arTrendYAxis = useMemo(() => {
    const values = arTrendData.map((row) => row.amount)
    return computeDynamicDollarAxis(values)
  }, [arTrendData])

  const agingBuckets = useMemo(() => {
    if (!data) return []
    return data.arAgingBuckets.buckets.map((bucket) => ({
      label: bucket.label,
      amount: bucket.value,
    }))
  }, [data])

  const agingChartData = useMemo(() => {
    if (agingView === 'Count') {
      return agingBuckets.map((bucket) => ({
        label: bucket.label,
        barValue: bucket.amount,
        topLabel: formatDollarK(bucket.amount),
      }))
    }
    const total = agingBuckets.reduce((sum, b) => sum + b.amount, 0) || 1
    return agingBuckets.map((bucket) => {
      const pct = Math.round((bucket.amount / total) * 1000) / 10
      return { label: bucket.label, barValue: pct, topLabel: `${pct}%` }
    })
  }, [agingBuckets, agingView])

  const agingYAxis = useMemo(() => {
    if (agingView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 20, 40, 60, 80, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    const values = agingBuckets.map((b) => b.amount)
    return {
      ...computeDynamicDollarAxis(values),
      tickFormatter: formatDollarK,
    }
  }, [agingBuckets, agingView])

  const pieData = useMemo(() => {
    if (!data) return []
    const insurance = data.insuranceAr.value
    const patient = data.patientAr.value
    const total = insurance + patient
    const insurancePct =
      total > 0 ? Math.round((insurance / total) * 1000) / 10 : 0
    const patientPct = total > 0 ? Math.round((patient / total) * 1000) / 10 : 0
    return [
      {
        name: 'Insurance AR',
        value: insurancePct,
        color: INSURANCE_AR_PIE_COLOR,
      },
      {
        name: 'Patient AR',
        value: patientPct,
        color: PATIENT_AR_PIE_COLOR,
      },
    ]
  }, [data])

  const pieLegend = useMemo(() => {
    if (!data) {
      return [
        {
          id: 'insurance',
          label: 'Insurance AR',
          amount: 0,
          percentage: 0,
          color: INSURANCE_AR_PIE_COLOR,
        },
        {
          id: 'patient',
          label: 'Patient AR',
          amount: 0,
          percentage: 0,
          color: PATIENT_AR_PIE_COLOR,
        },
      ]
    }
    const insurance = data.insuranceAr.value
    const patient = data.patientAr.value
    const total = insurance + patient
    const insurancePct =
      total > 0 ? Math.round((insurance / total) * 1000) / 10 : 0
    const patientPct = total > 0 ? Math.round((patient / total) * 1000) / 10 : 0
    return [
      {
        id: 'insurance',
        label: 'Insurance AR',
        amount: insurance,
        percentage: insurancePct,
        color: INSURANCE_AR_PIE_COLOR,
      },
      {
        id: 'patient',
        label: 'Patient AR',
        amount: patient,
        percentage: patientPct,
        color: PATIENT_AR_PIE_COLOR,
      },
    ]
  }, [data])

  const averageDaysData = useMemo(() => {
    if (!data) return []
    return data.averageDaysInArByPeriod.buckets.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: formatKpiDays(row.value),
    }))
  }, [data])

  const averageDaysYAxis = useMemo(() => {
    const values = averageDaysData.map((row) => row.barValue)
    return computeDaysAxis(values)
  }, [averageDaysData])

  const highBalanceRows = useMemo((): HighBalancePatientRow[] => {
    if (!data) return []
    return data.highBalancePatients.patients.map((row) => ({
      id: row.patientId,
      patient: row.patientName,
      balance: formatCurrency(row.balance),
      daysOutstanding: `${Math.round(row.daysOutstanding)} days`,
    }))
  }, [data])

  const highBalanceColumns: ReportDataTableColumn<HighBalancePatientRow>[] =
    useMemo(
      () => [
        { key: 'patient', label: 'Patient' },
        {
          key: 'balance',
          label: 'Balance',
          headerClassName: 'reports-receivables-risk__table-head--num',
          cellClassName: 'reports-receivables-risk__table-cell--num',
        },
        {
          key: 'daysOutstanding',
          label: 'Days Outstanding',
          headerClassName: 'reports-receivables-risk__table-head--num',
          cellClassName: 'reports-receivables-risk__table-cell--num',
        },
      ],
      [],
    )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalAr.value,
      data?.insuranceAr.value,
      data?.patientAr.value,
      data?.arOver90.value,
      data?.averageDaysInAr.value,
    ),
  )
  const arTrendEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.arTrendByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const agingEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.arAgingBuckets?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const pieEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(data?.insuranceAr.value, data?.patientAr.value),
  )
  const averageDaysEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.averageDaysInArByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const highBalanceEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.highBalancePatients?.patients ?? []).some((row) =>
      hasNonZeroDashboardValues(row.balance, row.daysOutstanding),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-receivables-risk">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Receivables Risk Analysis">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={RECEIVABLES_RISK_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-treatment-conversion-funnel__stack">
        <ReportExpandableSection sectionId="rra-not-table">
          {kpiEmpty ? (
            <DashboardSectionEmptyState />
          ) : (
            <ReportKpiGrid items={kpiItems} />
          )}

          <div className="reports-treatment-conversion-funnel__grid reports-receivables-risk__triple-grid">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading className="reports-sentence-case-title">
                    AR trend over time ({bucketLabel})
                  </ReportSectionHeading>
                </div>
                {arTrendEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={arTrendData}
                        margin={REPORT_LINE_CHART_MARGIN}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={arTrendYAxis.domain}
                          ticks={arTrendYAxis.ticks}
                          tickFormatter={arTrendYAxis.tickFormatter}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            formatDollarTooltip(value)
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Line
                          type="linear"
                          dataKey="amount"
                          stroke="var(--blue-500)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading className="reports-sentence-case-title">
                    AR aging buckets
                  </ReportSectionHeading>
                  {agingEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={agingView}
                      onChange={(value) =>
                        setAgingView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                {agingEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={agingChartData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={agingYAxis.domain}
                          ticks={agingYAxis.ticks}
                          tickFormatter={agingYAxis.tickFormatter}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(value: number) =>
                            agingView === 'Count'
                              ? formatDollarTooltip(value)
                              : `${value}%`
                          }
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 8, 8]}
                          maxBarSize={56}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card reports-receivables-risk__insurance-patient-ar-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading className="reports-sentence-case-title">
                    Insurance AR vs patient AR
                  </ReportSectionHeading>
                </div>
                {pieEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-receivables-risk__pie-body">
                    <div className="reports-receivables-risk__pie-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip
                            formatter={(value: number) => `${value}%`}
                            contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                          />
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={0}
                            outerRadius="100%"
                            paddingAngle={0}
                            startAngle={90}
                            endAngle={-270}
                            labelLine={false}
                          >
                            {pieData.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={entry.color}
                                stroke="var(--gray-white)"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="reports-receivables-risk__legend">
                      {pieLegend.map((slice) => (
                        <li
                          key={slice.id}
                          className="reports-receivables-risk__legend-row"
                        >
                          <span className="reports-receivables-risk__legend-label-group">
                            <span
                              className="reports-receivables-risk__legend-swatch"
                              style={{ backgroundColor: slice.color }}
                            />
                            <span className="reports-receivables-risk__legend-label">
                              {slice.label}
                            </span>
                          </span>
                          <span
                            className="reports-receivables-risk__legend-leader"
                            aria-hidden
                          />
                          <span className="reports-receivables-risk__legend-values">
                            <strong className="reports-receivables-risk__legend-amount">
                              ${slice.amount.toLocaleString('en-US')}
                            </strong>
                            <span className="reports-receivables-risk__legend-secondary">
                              {slice.percentage}%
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </div>
        </ReportExpandableSection>

        <div className="reports-treatment-conversion-funnel__grid reports-receivables-risk__split-grid">
          <ReportExpandableSection sectionId="rra-avg-days">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading className="reports-sentence-case-title">
                    Average days in AR ({bucketLabel})
                  </ReportSectionHeading>
                </div>
                {averageDaysEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={averageDaysData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={averageDaysYAxis.domain}
                          ticks={averageDaysYAxis.ticks}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(value: number) => `${value} days`}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          fill="var(--blue-400)"
                          radius={[8, 8, 8, 8]}
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="rra-high-balance">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column reports-patient-value-revenue__column--fill">
                <div className="reports-patient-base-health__chart-head">
                  <ReportFullViewTableTitle
                    sectionId="rra-high-balance"
                    title="High Balance Patients"
                    disableEnter={highBalanceEmpty}
                  />
                </div>
                {highBalanceEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-receivables-risk__table">
                    <ReportDataTable
                      key={
                        highBalanceFullView.isFullView
                          ? 'rra-high-balance-fv'
                          : 'rra-high-balance-card'
                      }
                      columns={highBalanceColumns}
                      rows={highBalanceRows}
                      rowKey={(row) => row.id}
                      initialPageSize={5}
                      className={[
                        'reports-receivables-risk__patient-table',
                        highBalanceFullView.isFullView
                          ? 'reports-patient-value-revenue__provider-table--full-view'
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      fullViewSectionId="rra-high-balance"
                      fullViewSearchWithFilter
                    />
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default ReceivablesRiskAnalysisReport
